import React, { Component } from 'react';
import { AlertNotice, Input, ModalInfo } from 'shared/components';
import { LocationEvent } from 'shared/models';
import Collapsible from 'react-collapsible';
import { Grid, Typography } from '@mui/material';
import { isEmpty, isInteger, isNull, isUndefined, omit } from 'lodash';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';

import './EditDate.scss';

const noLocationSelected = (locations) => !locations.some((loc) => loc.checked);

const getCapacity = (location) => (type) =>
  location.locationEventCapacities.find(({ capacityType }) => capacityType.type === type)
    ?.capacity ?? 0;

const isFieldEmpty = (val) => isNull(val) || isUndefined(val) || val === '';

const fields = [
  'REGULAR',
  'PLUS WRITING',
  'SPECIAL-AUTO',
  'SPECIAL-MANUAL',
  'ONLINE REGULAR',
  'BYOD REGULAR',
  'ONLINE SPECIAL-AUTO',
  'BYOD SPECIAL-AUTO',
  'ONLINE SPEC-MANUAL',
  'BYOD SPEC-MANUAL',
];

export class EditDate extends Component {
  constructor(props) {
    super(props);

    this.state = {
      capacityTypes: props.capacityTypes,
      locationsList: props.locationsList.map((loc) => ({
        ...loc,
        locationEventCapacities: fields
          .map((field) => {
            const capacityType = props.capacityTypes.find(({ type }) => type === field);
            if (!capacityType) return null;
            const capacity =
              (loc.locationEventCapacities || []).find(
                ({ capacityType: { type } }) => type === capacityType.type
              ) || {};
            return {
              capacity: null,
              capacityType,
              locationEventId: loc.id,
              registrationAssignCount: 0,
              ...capacity,
            };
          })
          .filter(Boolean),
      })),
      collapsibleOpen: false,
      event: props.event,
    };

    this.onChangeCapacity = this.onChangeCapacity.bind(this);
    this.selectBuilding = this.selectBuilding.bind(this);
    this.updateCapacity = this.updateCapacity.bind(this);
    this.handleTriggerText = this.handleTriggerText.bind(this);
    this.handleErrors = this.handleErrors.bind(this);
  }

  componentDidUpdate(prevProps) {
    if (prevProps.editModalOpen !== this.props.editModalOpen) {
      this.setState({
        collapsibleOpen: false,
      });
    }
    if (
      prevProps.locationsList !== this.props.locationsList ||
      prevProps.event !== this.props.event ||
      prevProps.capacityTypes !== this.props.capacityTypes
    ) {
      this.setState({
        locationsList: this.props.locationsList.map((loc) => ({
          ...loc,
          locationEventCapacities: fields
            .map((field) => {
              const capacityType = this.props.capacityTypes.find(({ type }) => type === field);
              if (!capacityType) return null;
              const capacity =
                (loc.locationEventCapacities || []).find(
                  ({ capacityType: { type } }) => type === capacityType.type
                ) || {};
              return {
                capacity: null,
                capacityType,
                locationEventId: loc.id,
                registrationAssignCount: 0,
                ...capacity,
              };
            })
            .filter(Boolean),
        })),
        event: this.props.event,
      });
    }
  }

  onChangeCapacity(e, location) {
    const { value, name } = e.target;

    // If the value is out of bounds or not a number, do nothing
    if (value && (+value > 9999 || +value < 0 || !isInteger(+value))) {
      return;
    }

    // If the user is prepending 0's, remove them
    if (value.length > 1 && +value[0] === 0) e.target.value = value.replace(/^0+/, '');

    const { locationsList } = this.state;
    const idx = locationsList.findIndex((loc) => loc.locationId === location.locationId);
    locationsList[idx].locationEventCapacities = locationsList[idx].locationEventCapacities.map(
      (lec) => {
        // If we are updating the REGULAR value, it must be split 60% REGULAR and the remainder PLUS WRITING
        if (name === 'REGULAR' && lec.capacityType.type === 'PLUS WRITING') {
          return {
            ...lec,
            capacity:
              value === '' ? value : +e.target.value - Number.parseInt(0.6 * +e.target.value, 10),
          };
        }
        if (lec.capacityType.type !== name) return lec;
        return {
          ...lec,
          capacity:
            value === ''
              ? value
              : lec.capacityType.type === 'REGULAR'
                ? Number.parseInt(0.6 * +e.target.value, 10)
                : +e.target.value,
        };
      }
    );
    locationsList[idx].error = '';
    locationsList[idx].errors = {};

    this.setState({
      locationsList,
    });
  }

  selectBuilding(e) {
    const { checked } = e.target;
    const { locationsList } = this.state;
    const idx = locationsList.findIndex((loc) => loc.locationId === +e.target.id);
    locationsList[idx] = {
      ...locationsList[idx],
      checked,
      capacityStandard: checked ? getCapacity(locationsList[idx])('REGULAR') : 0,
      capacityExtended: checked ? getCapacity(locationsList[idx])('SPECIAL-AUTO') : 0,
      capacityOnline: checked ? getCapacity(locationsList[idx])('ONLINE REGULAR') : 0,
      capacitySingleRoom: checked ? getCapacity(locationsList[idx])('SPECIAL-MANUAL') : 0,
      error: '',
      errors: {},
    };

    this.setState({
      locationsList,
    });
  }

  setEventCapacity(event, location) {
    const newEvent = {
      ...event,
      locationEvents: [...event.locationEvents],
    };

    const idx = newEvent.locationEvents.findIndex(
      (locEv) => locEv.locationId === location.locationId
    );
    if (idx > -1) {
      if (location.checked) {
        newEvent.locationEvents[idx] = {
          ...newEvent.locationEvents[idx],
          ...omit(location, 'error', 'checked', 'errors'),
          status: 'OPEN',
          locationEventCapacities: location.locationEventCapacities.filter(
            (lec) => lec.capacity > 0
          ),
          capacityStandard: getCapacity(location)('REGULAR'),
          capacityExtended: getCapacity(location)('SPECIAL-AUTO'),
          capacityOnline: getCapacity(location)('ONLINE REGULAR'),
          capacitySingleRoom: getCapacity(location)('SPECIAL-MANUAL'),
        };
      } else {
        newEvent.locationEvents[idx] = {
          ...newEvent.locationEvents[idx],
          ...omit(location, 'error', 'checked', 'errors'),
          status: 'CLOSED',
          locationEventCapacities: [],
        };
      }
    } else if (location.checked) {
      newEvent.locationEvents.push(
        new LocationEvent({
          ...location,
          locationEventCapacities: location.locationEventCapacities.filter(
            (lec) => lec.capacity > 0
          ),
          capacityStandard: getCapacity(location)('REGULAR'),
          capacityExtended: getCapacity(location)('SPECIAL-AUTO'),
          capacityOnline: getCapacity(location)('ONLINE REGULAR'),
          capacitySingleRoom: getCapacity(location)('SPECIAL-MANUAL'),
        })
      );
    }
    return newEvent;
  }

  handleErrors(location) {
    location.errors = {};
    location.error = '';
    let showCapacityError = false;

    //check emtpy fields
    fields.forEach((type) => {
      if (isFieldEmpty(getCapacity(location)(type))) {
        location.errors[type] = [''];
        showCapacityError = true;
      }
    });

    //capacity online > 0 then standard and extended can be 0
    //capacity online = 0 then standard and extended must be greater than 0
    if (
      !getCapacity(location)('ONLINE REGULAR') &&
      !getCapacity(location)('BYOD REGULAR') &&
      (!getCapacity(location)('REGULAR') || !getCapacity(location)('SPECIAL-AUTO'))
    ) {
      location.errors = {
        ...location.errors,
        capacityStandard:
          !getCapacity(location)('ONLINE REGULAR') &&
          !getCapacity(location)('BYOD REGULAR') &&
          !getCapacity(location)('REGULAR')
            ? ['']
            : '',
        capacityExtended:
          !getCapacity(location)('ONLINE REGULAR') &&
          !getCapacity(location)('BYOD REGULAR') &&
          !getCapacity(location)('SPECIAL-AUTO')
            ? ['']
            : '',
        capacityOnline:
          !getCapacity(location)('ONLINE REGULAR') &&
          !getCapacity(location)('BYOD REGULAR') &&
          !getCapacity(location)('REGULAR') &&
          !getCapacity(location)('SPECIAL-AUTO')
            ? ['']
            : '',
      };
      showCapacityError = true;
    }

    const onlineStandardTotal =
      Number(getCapacity(location)('ONLINE REGULAR') || 0) +
      Number(getCapacity(location)('BYOD REGULAR') || 0);
    const onlineExtendedTotal =
      Number(getCapacity(location)('ONLINE SPECIAL-AUTO') || 0) +
      Number(getCapacity(location)('BYOD SPECIAL-AUTO') || 0);
    const paperStandardTotal =
      Number(getCapacity(location)('REGULAR') || 0) + Number(getCapacity(location)('PLUS WRITING') || 0);
    const paperExtendedTotal = Number(getCapacity(location)('SPECIAL-AUTO') || 0);

    const validateStandardExtendedPair = ({
      standardTotal,
      extendedTotal,
      standardErrorField,
      standardMinErrorKey,
      standardRequiredError,
      extendedErrorField,
    }) => {
      if (standardTotal > 0 && standardTotal < 35) {
        location.errors[standardErrorField] = standardMinErrorKey;
      }
      if (standardTotal > 0 && extendedTotal < 10) {
        location.errors[extendedErrorField] = 'testCenter.reviewTestDates.extendedTimeError';
      }
      if (extendedTotal > 0 && standardTotal === 0) {
        location.errors[standardErrorField] = standardRequiredError;
      }
    };

    validateStandardExtendedPair({
      standardTotal: paperStandardTotal,
      extendedTotal: paperExtendedTotal,
      standardErrorField: 'REGULAR',
      standardMinErrorKey: 'testCenter.reviewTestDates.standardTimeDetails',
      standardRequiredError: [''],
      extendedErrorField: 'SPECIAL-AUTO',
    });

    validateStandardExtendedPair({
      standardTotal: onlineStandardTotal,
      extendedTotal: onlineExtendedTotal,
      standardErrorField: 'onlineStandard',
      standardMinErrorKey: 'testCenter.reviewTestDates.onlineStandardTimeDetails',
      standardRequiredError: 'testCenter.reviewTestDates.onlineStandardTimeDetails',
      extendedErrorField: 'onlineExtended',
    });

    if (showCapacityError) {
      location.error = this.props.t('testCenter.reviewTestDates.setCapacityErrorMessage');
    }
    return location.error || !isEmpty(location.errors) ? location : null;
  }

  updateCapacity() {
    const { capacityForAll, cycleEvents } = this.props;
    const locationsList = [...this.state.locationsList];
    let { event } = this.state;
    let selectedEvents = [];
    let updateEvents;

    if (isEmpty(event)) {
      selectedEvents = cycleEvents.filter((ev) => ev.selected);
    }
    const noLocationChecked = !locationsList.some((loc) => loc.checked);

    if (noLocationChecked) {
      if (capacityForAll) {
        this.props.optOutTestDates();
        return;
      }
      this.props.optForTestDate(event);
      return;
    }

    let error = false;

    locationsList.forEach((loc) => {
      if (loc.checked) {
        const newLoc = this.handleErrors(loc);
        if (!isEmpty(newLoc)) {
          loc = { ...newLoc };
          error = true;
        }
      }
      if (capacityForAll) {
        selectedEvents = selectedEvents.map((tcEv) => this.setEventCapacity(tcEv, loc));
      } else {
        event = this.setEventCapacity(event, loc);
      }
    });

    if (error) {
      this.setState({
        locationsList,
      });
      return;
    }

    this.setState({
      event,
    });

    if (isEmpty(event)) {
      updateEvents = cycleEvents.map((ev) => {
        if (ev.selected) {
          return selectedEvents.find((selEv) => selEv.testingEvent.id === ev.testingEvent.id);
        }
        return ev;
      });
    } else {
      updateEvents = event;
    }

    this.props.updateSelectedEvents(updateEvents);
  }

  handleTriggerText() {
    this.setState((prevState) => ({
      collapsibleOpen: !prevState.collapsibleOpen,
    }));
  }

  render() {
    const { event, editModalOpen, capacityForAll, cycleEvents } = this.props;
    const { capacityTypes, locationsList, collapsibleOpen } = this.state;

    if (!capacityForAll && !event.testingEvent) return null;
    return (
      !!locationsList?.length && (
        <ModalInfo
          modalIsOpen={editModalOpen}
          closeModal={this.props.closeModal}
          headerTitle="testCenter.reviewTestDates.editTestDateHeader"
          className="additional-person-modal"
          saveLabel="common.labels.save"
          onSubmit={this.updateCapacity}>
          <div className="edit-date-container">
            {capacityForAll ? (
              <div className="mb-4">
                <h5>{this.props.t('testCenter.reviewTestDates.sameCapacityLabel')}</h5>
                <p>{this.props.t('testCenter.reviewTestDates.addCapacityMessage')}</p>
                {noLocationSelected(locationsList) ? (
                  <AlertNotice
                    message={this.props.t('testCenter.reviewTestDates.checkLocationsInfoMessage')}
                    type="info"
                  />
                ) : (
                  <AlertNotice
                    message={this.props.t('testCenter.reviewTestDates.sameCapacityInfo')}
                    type="info"
                  />
                )}
                <Collapsible
                  handleTriggerClick={this.handleTriggerText}
                  open={collapsibleOpen}
                  trigger={
                    collapsibleOpen
                      ? this.props.t('testCenter.reviewTestDates.hideTestDates')
                      : this.props.t('testCenter.reviewTestDates.showTestDates')
                  }>
                  <div className="collapsible-dates">
                    {cycleEvents.map(
                      (ev) =>
                        ev.selected && (
                          <p className="font-size-12" key={ev.testingEvent.id}>
                            {ev.testingEvent.name}
                          </p>
                        )
                    )}
                  </div>
                </Collapsible>
              </div>
            ) : (
              <div className="mb-4">
                <h5>{event.testingEvent.name}</h5>
                <p
                  dangerouslySetInnerHTML={{
                    __html: this.props.t('testCenter.reviewTestDates.addCapacityMessage'),
                  }}
                />
                {noLocationSelected(locationsList) && (
                  <AlertNotice
                    message={this.props.t('testCenter.reviewTestDates.checkLocationsInfoMessage')}
                    type="info"
                  />
                )}
              </div>
            )}
            <div className="locations-list">
              {locationsList?.map((location) => (
                <div className="date-location" key={location.locationId}>
                  <Input
                    type="checkbox"
                    id={`${location.locationId}`}
                    name={`${location.locationId}`}
                    checked={location.checked}
                    onChange={this.selectBuilding}>
                    <label htmlFor={location.locationId}>
                      {location.name}
                      {location.area?.length > 0 && `, ${location.area}`}
                    </label>
                  </Input>
                  {location.error && <p className="text-error ps-4 mb-2">{location.error}</p>}
                  {location.checked && (
                    <div className="capacity-input-group">
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                          <Grid
                            alignItems="stretch"
                            container
                            direction="column"
                            justifyContent="flex-start"
                            spacing={2}>
                            <Grid item xs={12}>
                              <Typography variant="h6">
                                {this.props.t(
                                  'testCenter.reviewTestDates.paperCapacityColumnLabel'
                                )}{' '}
                                (
                                <span className="capacity-highlight">
                                  {location.locationEventCapacities
                                    .filter(({ capacityType: { type } }) =>
                                      [
                                        'REGULAR',
                                        'PLUS WRITING',
                                        'SPECIAL-AUTO',
                                        'SPECIAL-MANUAL',
                                      ].includes(type)
                                    )
                                    .reduce(
                                      (acc, { capacity }) => acc + (Number(capacity) || 0),
                                      0
                                    )}
                                  {/* */}
                                </span>
                                )
                              </Typography>
                            </Grid>
                            <Grid item xs={12}>
                              <Input
                                type="number"
                                name="REGULAR"
                                key={`${location.locationId}-REGULAR`}
                                value={
                                  getCapacity(location)('REGULAR') +
                                  getCapacity(location)('PLUS WRITING')
                                }
                                label="testCenter.reviewTestDates.standardTimeLabel"
                                infoText={this.props.t(
                                  'testCenter.reviewTestDates.standardTimeDetails'
                                )}
                                errors={location.errors?.REGULAR}
                                onChange={(e) => this.onChangeCapacity(e, location)}
                              />
                            </Grid>
                            <Grid item xs={12}>
                              <Input
                                type="number"
                                name="SPECIAL-AUTO"
                                key={`${location.locationId}-SPECIAL-AUTO`}
                                value={getCapacity(location)('SPECIAL-AUTO')}
                                label="testCenter.reviewTestDates.extendedTimeLabel"
                                infoText={this.props.t(
                                  'testCenter.reviewTestDates.extendedTimeDetails'
                                )}
                                errors={location.errors?.['SPECIAL-AUTO']}
                                onChange={(e) => this.onChangeCapacity(e, location)}
                              />
                            </Grid>
                            <Grid item xs={12}>
                              <Input
                                type="number"
                                name="SPECIAL-MANUAL"
                                maxLength="1"
                                key={`${location.locationId}-SPECIAL-MANUAL`}
                                value={getCapacity(location)('SPECIAL-MANUAL')}
                                label="testCenter.reviewTestDates.singleRoomLabel"
                                infoText={this.props.t(
                                  'testCenter.reviewTestDates.singleRoomDetails'
                                )}
                                errors={location.errors?.['SPECIAL-MANUAL']}
                                onChange={(e) => this.onChangeCapacity(e, location)}
                              />
                            </Grid>
                          </Grid>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <Grid
                            alignItems="stretch"
                            container
                            direction="column"
                            justifyContent="flex-start"
                            spacing={2}>
                            <Grid item xs={12}>
                              <Typography variant="h6">
                                {this.props.t(
                                  'testCenter.reviewTestDates.onlineCapacityColumnLabel'
                                )}{' '}
                                (
                                <span className="capacity-highlight">
                                  {location.locationEventCapacities
                                    .filter(({ capacityType: { type } }) =>
                                      [
                                        'ONLINE REGULAR',
                                        'BYOD REGULAR',
                                        'ONLINE SPECIAL-AUTO',
                                        'BYOD SPECIAL-AUTO',
                                        'ONLINE SPEC-MANUAL',
                                        'BYOD SPEC-MANUAL',
                                      ].includes(type)
                                    )
                                    .reduce(
                                      (acc, { capacity }) => acc + (Number(capacity) || 0),
                                      0
                                    )}
                                </span>
                                )
                              </Typography>
                            </Grid>
                            <Grid item xs={12}>
                              <Typography
                                variant="body1"
                                className={`online-subsection-title ${
                                  location.errors?.onlineStandard ? 'text-error' : ''
                                }`}>
                                {this.props.t('testCenter.reviewTestDates.standardTimeLabel')} (
                                <span className="capacity-highlight online-subsection-total">
                                  {location.locationEventCapacities
                                    .filter(({ capacityType: { type } }) =>
                                      ['ONLINE REGULAR', 'BYOD REGULAR'].includes(type)
                                    )
                                    .reduce(
                                      (acc, { capacity }) => acc + (Number(capacity) || 0),
                                      0
                                    )}
                                </span>
                                )
                              </Typography>
                              <span
                                className={`online-helper-text ${
                                  location.errors?.onlineStandard
                                    ? 'text-error font-size-12'
                                    : ''
                                }`}>
                                {this.props.t(
                                  location.errors?.onlineStandard ||
                                    'testCenter.reviewTestDates.onlineStandardTimeDetails'
                                )}
                              </span>
                              {capacityTypes.some(({ type }) => type === 'ONLINE REGULAR') && (
                                <Input
                                  type="number"
                                  name="ONLINE REGULAR"
                                  key={`${location.locationId}-ONLINE REGULAR`}
                                  value={getCapacity(location)('ONLINE REGULAR')}
                                  label="testCenter.reviewTestDates.siteDeviceSeatsLabel"
                                  errors={location.errors?.['ONLINE REGULAR']}
                                  onChange={(e) => this.onChangeCapacity(e, location)}
                                />
                              )}
                              {capacityTypes.some(({ type }) => type === 'BYOD REGULAR') && (
                                <Input
                                  type="number"
                                  name="BYOD REGULAR"
                                  key={`${location.locationId}-BYOD REGULAR`}
                                  value={getCapacity(location)('BYOD REGULAR')}
                                  label="testCenter.reviewTestDates.byodSeatsLabel"
                                  errors={location.errors?.['BYOD REGULAR']}
                                  onChange={(e) => this.onChangeCapacity(e, location)}
                                />
                              )}
                            </Grid>
                            <Grid item xs={12}>
                              <Typography
                                variant="body1"
                                className={`online-subsection-title ${
                                  location.errors?.onlineExtended ? 'text-error' : ''
                                }`}>
                                {this.props.t('testCenter.reviewTestDates.extendedTimeLabel')} (
                                <span className="capacity-highlight online-subsection-total">
                                  {location.locationEventCapacities
                                    .filter(({ capacityType: { type } }) =>
                                      ['ONLINE SPECIAL-AUTO', 'BYOD SPECIAL-AUTO'].includes(type)
                                    )
                                    .reduce(
                                      (acc, { capacity }) => acc + (Number(capacity) || 0),
                                      0
                                    )}
                                </span>
                                )
                              </Typography>
                              <span
                                className={`online-helper-text ${
                                  location.errors?.onlineExtended ? 'text-error font-size-12' : ''
                                }`}>
                                {this.props.t(
                                  location.errors?.onlineExtended ||
                                    'testCenter.reviewTestDates.extendedTimeDetails'
                                )}
                              </span>
                              {capacityTypes.some(({ type }) => type === 'ONLINE SPECIAL-AUTO') && (
                                <Input
                                  type="number"
                                  name="ONLINE SPECIAL-AUTO"
                                  key={`${location.locationId}-ONLINE SPECIAL-AUTO`}
                                  value={getCapacity(location)('ONLINE SPECIAL-AUTO')}
                                  label="testCenter.reviewTestDates.siteDeviceSeatsLabel"
                                  errors={location.errors?.['ONLINE SPECIAL-AUTO']}
                                  onChange={(e) => this.onChangeCapacity(e, location)}
                                />
                              )}
                              {capacityTypes.some(({ type }) => type === 'BYOD SPECIAL-AUTO') && (
                                <Input
                                  type="number"
                                  name="BYOD SPECIAL-AUTO"
                                  key={`${location.locationId}-BYOD SPECIAL-AUTO`}
                                  value={getCapacity(location)('BYOD SPECIAL-AUTO')}
                                  label="testCenter.reviewTestDates.byodSeatsLabel"
                                  errors={location.errors?.['BYOD SPECIAL-AUTO']}
                                  onChange={(e) => this.onChangeCapacity(e, location)}
                                />
                              )}
                            </Grid>
                            <Grid item xs={12}>
                              <Typography variant="body1" className="online-subsection-title">
                                {this.props.t('testCenter.reviewTestDates.singleRoomLabel')} (
                                <span className="capacity-highlight online-subsection-total">
                                  {location.locationEventCapacities
                                    .filter(({ capacityType: { type } }) =>
                                      ['ONLINE SPEC-MANUAL', 'BYOD SPEC-MANUAL'].includes(type)
                                    )
                                    .reduce(
                                      (acc, { capacity }) => acc + (Number(capacity) || 0),
                                      0
                                    )}
                                </span>
                                )
                              </Typography>
                              <span className="online-helper-text">
                                {this.props.t('testCenter.reviewTestDates.singleRoomDetails')}
                              </span>
                              {capacityTypes.some(({ type }) => type === 'ONLINE SPEC-MANUAL') && (
                                <Input
                                  type="number"
                                  maxLength="1"
                                  name="ONLINE SPEC-MANUAL"
                                  key={`${location.locationId}-ONLINE SPEC-MANUAL`}
                                  value={getCapacity(location)('ONLINE SPEC-MANUAL')}
                                  label="testCenter.reviewTestDates.siteDeviceSeatsLabel"
                                  errors={location.errors?.['ONLINE SPEC-MANUAL']}
                                  onChange={(e) => this.onChangeCapacity(e, location)}
                                />
                              )}
                              {capacityTypes.some(({ type }) => type === 'BYOD SPEC-MANUAL') && (
                                <Input
                                  type="number"
                                  maxLength="1"
                                  name="BYOD SPEC-MANUAL"
                                  key={`${location.locationId}-BYOD SPEC-MANUAL`}
                                  value={getCapacity(location)('BYOD SPEC-MANUAL')}
                                  label="testCenter.reviewTestDates.byodSeatsLabel"
                                  errors={location.errors?.['BYOD SPEC-MANUAL']}
                                  onChange={(e) => this.onChangeCapacity(e, location)}
                                />
                              )}
                            </Grid>
                          </Grid>
                        </Grid>
                      </Grid>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </ModalInfo>
      )
    );
  }
}

EditDate.propTypes = {
  event: PropTypes.shape({
    testingEvent: PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
    }),
    locationEvents: PropTypes.arrayOf(PropTypes.shape({})),
    selected: PropTypes.bool,
  }),
  editModalOpen: PropTypes.bool.isRequired,
  closeModal: PropTypes.func.isRequired,
  capacityForAll: PropTypes.bool.isRequired,
  locationsList: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      locationId: PropTypes.number,
      name: PropTypes.string,
      area: PropTypes.string,
      checked: PropTypes.bool,
      locationEventCapacities: PropTypes.arrayOf(
        PropTypes.shape({
          capacity: PropTypes.number,
          capacityType: PropTypes.shape({
            type: PropTypes.string,
          }),
          locationEventId: PropTypes.number,
          registrationAssignCount: PropTypes.number,
        })
      ),
    })
  ).isRequired,
  capacityTypes: PropTypes.arrayOf(
    PropTypes.shape({
      type: PropTypes.string,
    })
  ).isRequired,
  cycleEvents: PropTypes.arrayOf(
    PropTypes.shape({
      testingEvent: PropTypes.shape({
        id: PropTypes.number,
        name: PropTypes.string,
      }),
      selected: PropTypes.bool,
    })
  ),
  optOutTestDates: PropTypes.func,
  optForTestDate: PropTypes.func,
  updateSelectedEvents: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
};

export default withTranslation(null, { withRef: true })(EditDate);
